import { LoaderFunction } from 'react-router-dom';

// Utils
import { AuthContextType } from '../../../../../hooks/useAuth';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import {
  ACCIDENT_EDIT_SCOPE,
  ACCIDENT_MEDIA_SCOPE,
} from '../../../../../utils/draftScopes';
import { readAccidentEditCopy } from '../../../../../utils/accidentEditDraft';

// Types
import { AddBasicInfoAccidentWithDynamicKeys } from './types';

// Constants
import { formVehicleNaming } from '../../../../../components/forms/AddVehicleForm/constants';
import { formAccidentNaming } from '../../../../../components/forms/AddBasicInfoAccidentForm/constants';
import { Accident } from '../../../../../types/accident';

// Sentry
import * as Sentry from '@sentry/react';

// Selectors
import { selectVehiclesAndDamagesFromAccident } from '../../../../../gql/selectors/accidents';

const loadVehiclesData = async (orderId: string) => {
  try {
    const vehiclesData = await selectVehiclesAndDamagesFromAccident(orderId);

    return vehiclesData;
  } catch (error) {
    Sentry.captureException(error);
    console.log('> Vehicles loading error:', error);
    return true;
  }
};

export const VehicleEditLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async ({ params }) => {
  // 🔴 Antes de cualquier `await`: borradores y croquis del accidente de la
  // URL (ver el loader del paso 1). Este paso sacaba la Order de la copia del
  // paso 1: con atrás, la URL de A pintaba los vehículos de B y su «Guardar»
  // escribía en B (R5).
  claimDraftScope(ACCIDENT_EDIT_SCOPE, params.id);
  claimDraftScope(ACCIDENT_MEDIA_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    // La Order es la de la URL, nunca la de la copia.
    const orderParentId = params.id;

    const sessionStorageData =
      readAccidentEditCopy<AddBasicInfoAccidentWithDynamicKeys>(
        formVehicleNaming.EDIT_VEHICLE_FORM,
        orderParentId
      );
    const accidentData = readAccidentEditCopy<Accident>(
      formAccidentNaming.EDIT_ACCIDENT_FORM,
      orderParentId
    );

    // Sin el paso 1 de ESTA Order no hay con qué guardar: vuelta al paso 1.
    if (!orderParentId || !accidentData || !userData) {
      return null;
    }

    const vehiclesData = await loadVehiclesData(orderParentId);

    if (sessionStorageData) {
      return {
        ...(vehiclesData as object),
        ...sessionStorageData,
        orderParentRoid: accidentData.roid,
        orderParentId,
      };
    }

    return {
      orderParentRoid: accidentData.roid,
      orderParentId,
      ...(vehiclesData as object),
      dataToCompare: vehiclesData,
    };
  };

  return { data: load() };
};
